import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';

import { deleteCustomFormResponse, upsertCustomFormResponse } from 'api/CustomForms';
import { createMedication, deleteMedication } from 'api/Medication';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';

import { showAlert } from 'components/shared/Alert/Alert';
import { translateMedicationToWeightLossData } from 'components/shared/Forms/Custom/Provider/WeightLoss/lib/helpers';
import {
  CONFIRMATION_MODAL,
  MEDICATION_ACTION_TYPES,
  MEDICATION_STATUS,
  VERADIGM_MEDICATION_READ_ONLY_TOOLTIP
} from 'components/shared/Forms/Custom/ProviderMedications/lib/constants';
import { getMedicationDeleteContextMessage } from 'components/shared/Forms/Custom/ProviderMedications/lib/helpers';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import { clinicalNote as clinicalNoteState } from 'components/state';

const Actions = ({ data, customProps }) => {
  const [confirmationModal, setConfirmationModal] = useState(CONFIRMATION_MODAL);

  const clinicalNote = useRecoilValue(clinicalNoteState);

  const { id: patientId, appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { saveNoteRef, setOverviewData } = useClinicalNoteContext() || {};
  const queryClient = useQueryClient();

  const handleStatusLabel = data?.status === MEDICATION_STATUS.ACTIVE ? 'Inactive' : 'Active';
  const isVeradigmMedication = Boolean(data?.veradigm_id);

  const options = [
    {
      icon: 'status-mono',
      color: data?.status === MEDICATION_STATUS.ACTIVE ? 'danger' : 'primary',
      stroke: true,
      label: `Mark ${handleStatusLabel}`,
      disabled: isVeradigmMedication,
      tooltip: isVeradigmMedication ? VERADIGM_MEDICATION_READ_ONLY_TOOLTIP : null,
      onClick: () =>
        setConfirmationModal({ open: true, type: MEDICATION_ACTION_TYPES.UPDATE_STATUS })
    },
    {
      icon: 'edit-2-linear',
      label: 'Edit',
      disabled: isVeradigmMedication,
      tooltip: isVeradigmMedication ? VERADIGM_MEDICATION_READ_ONLY_TOOLTIP : null,
      onClick: () => onHandleEdit()
    },
    {
      icon: 'trash-mono',
      label: 'Delete',
      disabled: isVeradigmMedication,
      tooltip: isVeradigmMedication ? VERADIGM_MEDICATION_READ_ONLY_TOOLTIP : null,
      onClick: () => setConfirmationModal({ open: true, type: MEDICATION_ACTION_TYPES.DELETE })
    }
  ];

  const fromClinicalNote = () => {
    if (location.pathname.includes('clinical-note')) {
      return true;
    }
    return false;
  };

  const onHandleDelete = async () => {
    if (fromClinicalNote() && !clinicalNote?.id) await saveNoteRef?.current?.onSaveNote?.();

    if (data?.medication_id || (data?.id && !customProps.fromWeightLoss)) {
      await mutateDeleteMedication.mutateAsync();
    }
    if (data?.weight_loss_medication_id || data?.cfr_id) {
      await mutatateDeleteCustomFormResponse.mutateAsync();
    }
    if (customProps?.gridApi) {
      customProps.gridApi.deselectAll();
    }
    setConfirmationModal(CONFIRMATION_MODAL);
  };

  const onHandleEdit = () => {
    customProps?.setShowMedicationModal({ open: true, data });
    if (customProps?.gridApi) {
      customProps.gridApi.deselectAll();
    }
  };

  const onHandleUpdateStatus = async () => {
    if (fromClinicalNote() && !clinicalNote?.id) await saveNoteRef?.current?.onSaveNote?.();

    await upsertFormFunctionMutation.mutateAsync({
      patientId: customProps?.patientId || patientId,
      clinicalNoteId: clinicalNote?.id,
      appointmentId,
      practice_id: data?.practice_id,
      medication: {
        medications: [
          {
            ...data,
            status:
              data?.status === MEDICATION_STATUS.ACTIVE
                ? MEDICATION_STATUS.INACTIVE
                : MEDICATION_STATUS.ACTIVE
          }
        ]
      }
    });

    const formattedWhlData = translateMedicationToWeightLossData({
      medication: data,
      medication_id: data?.id,
      weight: data?.weight
    });

    if (data?.weight_loss_medication_id) {
      await upsertWeightLossMutation.mutateAsync({
        patientId,
        appointmentId,
        clinicalNoteId: clinicalNote?.id,
        response: {
          row: [
            {
              ...formattedWhlData,
              status:
                formattedWhlData?.status === MEDICATION_STATUS.ACTIVE
                  ? MEDICATION_STATUS.INACTIVE
                  : MEDICATION_STATUS.ACTIVE
            }
          ]
        },
        type: 'weightLossAdministration',
        formId: data?.weight_loss_medication_id
      });
    }
    if (customProps?.gridApi) {
      customProps.gridApi.deselectAll();
    }
    setConfirmationModal(CONFIRMATION_MODAL);
  };

  const mutateDeleteMedication = useMutation({
    mutationFn: () =>
      deleteMedication(navigate, {
        medicationId: data?.id,
        version: data?.version,
        clinicalNoteId: clinicalNote?.id,
        appointmentId
      }),
    onSuccess: async ({ code, error }) => {
      if (code !== 0) {
        showAlert({
          title: 'Medications',
          message: error,
          color: 'warning'
        });
        return;
      }

      showAlert({
        title: `Medication`,
        message: 'Medication deleted successfully',
        color: 'success'
      });

      queryClient.invalidateQueries(['medications']);
      queryClient.invalidateQueries(['medication']);
    }
  });

  const mutatateDeleteCustomFormResponse = useMutation({
    mutationFn: () =>
      deleteCustomFormResponse(navigate, {
        customFormId: data?.weight_loss_medication_id || data?.cfr_id,
        customFormVersion: data?.version,
        clinicalNoteId: clinicalNote?.id,
        appointmentId
      }),
    onSuccess: async ({ code, error }) => {
      if (code !== 0) {
        showAlert({
          title: 'Delete Weight Loss Medication',
          message: error,
          color: 'warning'
        });
        return;
      }

      const idToDelete = data?.weight_loss_medication_id || data?.cfr_id;
      setOverviewData((prev) => ({
        ...prev,
        weightLossAdministration: prev?.weightLossAdministration?.filter(
          (weightLoss) => weightLoss?.id !== idToDelete
        )
      }));
      queryClient.invalidateQueries(['medications']);
      queryClient.invalidateQueries(['medication']);
    }
  });

  const upsertFormFunctionMutation = useMutation((data) => createMedication(navigate, data), {
    onSettled: (upsertData) => {
      const { code, error = null } = upsertData || {};

      if (code !== 0) {
        showAlert({
          title: 'Medication',
          message: error ?? 'Medication creation failed! Please contact support.',
          color: 'danger'
        });

        return;
      }

      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication'] });
    }
  });

  const upsertWeightLossMutation = useMutation((data) => upsertCustomFormResponse(navigate, data), {
    onSettled: (data) => {
      const { code, error = null, newResponse } = data || {};

      if (code !== 0) {
        showAlert({
          title: 'Syncing Medication to Weight Loss Administration',
          message: error ?? 'Syncing status failed! Please contact support.',
          color: 'danger'
        });

        return;
      }

      if (fromClinicalNote() && data?.weight_loss_medication_id) {
        setOverviewData((prev) => ({
          ...prev,
          weightLossAdministration: prev?.weightLossAdministration?.map((weightLoss) => {
            if (weightLoss?.id === data?.weight_loss_medication_id) {
              return newResponse;
            }
            return weightLoss;
          })
        }));
      }

      queryClient.invalidateQueries({ queryKey: ['medications'] });
      queryClient.invalidateQueries({ queryKey: ['medication'] });
    }
  });

  return (
    <div className="flex h-full w-full items-center justify-center">
      <Popover
        options={options}
        optionIconColor="primary"
        position="left"
        isFixed
        icon="context-menu-vertical"
      />

      {confirmationModal?.open && confirmationModal?.type === MEDICATION_ACTION_TYPES.DELETE && (
        <Confirm
          icon="info-filled"
          iconColor="danger"
          iconSize={42}
          variant="danger"
          title="Delete Medication"
          message={`Are you sure you want to delete this medication? ${getMedicationDeleteContextMessage(data)}`}
          handleOpen={!!confirmationModal?.open}
          handleClose={() => setConfirmationModal(CONFIRMATION_MODAL)}
          footerActions={{
            primary: {
              text: 'Delete',
              onClick: onHandleDelete,
              color: 'danger',
              isLoading:
                mutateDeleteMedication?.isLoading || mutatateDeleteCustomFormResponse?.isLoading,
              isDisabled:
                mutateDeleteMedication?.isLoading || mutatateDeleteCustomFormResponse?.isLoading
            }
          }}
        />
      )}

      {confirmationModal?.open &&
        confirmationModal?.type === MEDICATION_ACTION_TYPES.UPDATE_STATUS && (
          <Confirm
            icon="info-filled"
            iconSize={42}
            variant="warning"
            title={`Mark ${handleStatusLabel} Medication`}
            message={`Are you sure you want to mark this medication as ${handleStatusLabel}?`}
            handleOpen={!!confirmationModal?.open}
            handleClose={() => setConfirmationModal(CONFIRMATION_MODAL)}
            footerActions={{
              primary: {
                text: `Mark ${handleStatusLabel}`,
                onClick: onHandleUpdateStatus,
                color: 'warning',
                isLoading: upsertFormFunctionMutation?.isLoading,
                isDisabled: upsertFormFunctionMutation?.isLoading
              }
            }}
          />
        )}
    </div>
  );
};

export default Actions;
